/**
 * Workflow Templates Management
 * 
 * Admin page to create and manage workflow templates for all types.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { workflowAPI, rolesAPI, organizationAPI, documentTemplatesAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, Trash2, Edit3, GripVertical, FileText, UserPlus, UserMinus,
  Loader2, Settings, Star, MapPin, Clock, Baby, ShieldCheck, GraduationCap,
  Zap, Info,
} from 'lucide-react';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, arrayMove, useSortable,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function SortableTaskItem({ task, index, onEdit, onDelete, canEdit }) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: task.id, disabled: !canEdit });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 p-3 bg-secondary/30 rounded-lg group"
      data-testid={`task-${task.id}`}
    >
      {canEdit && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 -ml-1 rounded hover:bg-muted/50 text-muted-foreground touch-none"
          title="Zum Sortieren ziehen"
          data-testid={`task-drag-handle-${task.id}`}
        >
          <GripVertical className="h-4 w-4" />
        </button>
      )}
      <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium">{index + 1}</span>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{task.name}</p>
        <div className="flex items-center gap-2 text-xs text-muted-foreground flex-wrap">
          {task.assigned_role && <Badge variant="outline" className="text-xs">{task.assigned_role}</Badge>}
          {task.deadline_days && <span>Tag {task.deadline_days}</span>}
          {task.requires_document && (
            <Badge variant="secondary" className="text-xs"><FileText className="h-3 w-3 mr-1" />{task.document_name || 'Dokument'}</Badge>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEdit(task)}>
            <Edit3 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500" onClick={() => onDelete(task.id)}>
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

const ROLE_OPTIONS = [
  { value: 'Personaler/in', label: 'HR / Personal' },
  { value: 'IT-Mitarbeiter/in', label: 'IT' },
  { value: 'QMB', label: 'QMB' },
  { value: 'Standortleiter/in', label: 'Standortleiter/in' },
  { value: 'Niederlassungsleiter/in', label: 'Niederlassungsleiter/in' },
  { value: 'Verwaltungsmitarbeiter/in', label: 'Verwaltung' },
];

const WORKFLOW_TYPES = [
  { value: 'onboarding', label: 'On-Boarding', icon: UserPlus, color: 'text-green-500' },
  { value: 'offboarding', label: 'Off-Boarding', icon: UserMinus, color: 'text-orange-500' },
  { value: 'versetzung', label: 'Versetzung', icon: MapPin, color: 'text-blue-500' },
  { value: 'probezeit', label: 'Probezeit', icon: Clock, color: 'text-purple-500' },
  { value: 'mutterschutz', label: 'Mutterschutz', icon: Baby, color: 'text-pink-500' },
  { value: 'zertifikat', label: 'Zertifikate', icon: ShieldCheck, color: 'text-amber-500' },
  { value: 'pflichtschulung', label: 'Schulungen', icon: GraduationCap, color: 'text-teal-500' },
  { value: 'vertragsende', label: 'Vertragsende', icon: FileText, color: 'text-red-500' },
];

const AUTO_TRIGGERABLE = ['probezeit', 'zertifikat', 'pflichtschulung', 'vertragsende'];

export default function WorkflowTemplates() {
  const { isAdmin, hasPermission } = useAuth();
  const canEdit = isAdmin || hasPermission('settings', 'edit');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('onboarding');
  const [triggers, setTriggers] = useState([]);
  const [triggerSaving, setTriggerSaving] = useState(null);
  
  // Template dialog
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [templateForm, setTemplateForm] = useState({
    name: '', description: '', workflow_type: 'onboarding', is_default: false,
    branch_ids: [], location_ids: [],
  });
  const [saving, setSaving] = useState(false);
  const [roleDefs, setRoleDefs] = useState([]);
  const [docTemplates, setDocTemplates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Task dialog
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    name: '', description: '', assigned_role: '', deadline_days: '',
    requires_document: false, document_name: '', document_template_id: '',
  });
  const [savingTask, setSavingTask] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleTaskDragEnd = async (template, event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const ordered = [...(template.tasks || [])].sort((a, b) => a.order - b.order);
    const oldIndex = ordered.findIndex(t => t.id === active.id);
    const newIndex = ordered.findIndex(t => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newOrder = arrayMove(ordered, oldIndex, newIndex);
    const newTaskIds = newOrder.map(t => t.id);
    // Optimistisches Update
    const prevTemplates = templates;
    setTemplates(prev => prev.map(t =>
      t.id === template.id
        ? { ...t, tasks: newOrder.map((task, i) => ({ ...task, order: i })) }
        : t
    ));
    try {
      await workflowAPI.reorderTasks(template.id, newTaskIds);
    } catch (error) {
      console.error('Reorder failed:', error);
      toast.error('Reihenfolge konnte nicht gespeichert werden');
      setTemplates(prevTemplates);
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [templatesRes, triggersRes, rolesRes, docsRes, structureRes] = await Promise.all([
        workflowAPI.getTemplates(),
        workflowAPI.getTriggers().catch(() => ({ data: [] })),
        rolesAPI.getAll().catch(() => ({ data: [] })),
        documentTemplatesAPI.list().catch(() => ({ data: [] })),
        organizationAPI.getStructure().catch(() => ({ data: { branches: [] } })),
      ]);
      setTemplates(templatesRes.data);
      setTriggers(triggersRes.data);
      setRoleDefs(rolesRes.data || []);
      setDocTemplates(docsRes.data || []);
      const brs = structureRes.data?.branches || [];
      setBranches(brs.map(b => ({ id: b.id, name: b.name })));
      setLocations(brs.flatMap(b => (b.locations || []).map(l => ({ id: l.id, name: l.name, branch_name: b.name }))));
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Vorlagen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const filteredTemplates = templates.filter(t => t.workflow_type === activeTab);
  const currentTrigger = triggers.find(t => t.workflow_type === activeTab);
  const isAutoTriggerable = AUTO_TRIGGERABLE.includes(activeTab);

  // Auto-trigger handlers
  const handleTriggerToggle = async (enabled) => {
    setTriggerSaving(activeTab);
    try {
      await workflowAPI.updateTrigger(activeTab, { enabled });
      setTriggers(prev => prev.map(t =>
        t.workflow_type === activeTab ? { ...t, enabled } : t
      ));
      toast.success(enabled ? 'Automatische Auslösung aktiviert' : 'Automatische Auslösung deaktiviert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setTriggerSaving(null); }
  };

  const handleTriggerDaysChange = async (days) => {
    const parsed = parseInt(days);
    if (isNaN(parsed) || parsed < 1) return;
    setTriggerSaving(activeTab);
    try {
      await workflowAPI.updateTrigger(activeTab, { trigger_days_before: parsed });
      setTriggers(prev => prev.map(t =>
        t.workflow_type === activeTab ? { ...t, trigger_days_before: parsed } : t
      ));
      toast.success('Frist gespeichert');
    } catch { toast.error('Fehler beim Speichern'); }
    finally { setTriggerSaving(null); }
  };

  // Template handlers
  const openNewTemplateDialog = () => {
    setEditingTemplate(null);
    setTemplateForm({ name: '', description: '', workflow_type: activeTab, is_default: false, branch_ids: [], location_ids: [] });
    setTemplateDialogOpen(true);
  };

  const openEditTemplateDialog = (template) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name, description: template.description || '',
      workflow_type: template.workflow_type, is_default: template.is_default,
      branch_ids: template.branch_ids || [], location_ids: template.location_ids || [],
    });
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) { toast.error('Bitte Namen eingeben'); return; }
    setSaving(true);
    try {
      if (editingTemplate) {
        await workflowAPI.updateTemplate(editingTemplate.id, {
          name: templateForm.name, description: templateForm.description || null,
          is_default: templateForm.is_default,
          branch_ids: templateForm.branch_ids, location_ids: templateForm.location_ids,
        });
        toast.success('Vorlage aktualisiert');
      } else {
        await workflowAPI.createTemplate({
          name: templateForm.name, description: templateForm.description || null,
          workflow_type: templateForm.workflow_type, is_default: templateForm.is_default, tasks: [],
          branch_ids: templateForm.branch_ids, location_ids: templateForm.location_ids,
        });
        toast.success('Vorlage erstellt');
      }
      setTemplateDialogOpen(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSaving(false); }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Vorlage wirklich löschen?')) return;
    try {
      await workflowAPI.deleteTemplate(templateId);
      toast.success('Vorlage gelöscht');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  // Task handlers
  const openNewTaskDialog = (templateId) => {
    setSelectedTemplateId(templateId);
    setEditingTask(null);
    setTaskForm({ name: '', description: '', assigned_role: '', deadline_days: '', requires_document: false, document_name: '', document_template_id: '' });
    setTaskDialogOpen(true);
  };

  const openEditTaskDialog = (templateId, task) => {
    setSelectedTemplateId(templateId);
    setEditingTask(task);
    setTaskForm({
      name: task.name, description: task.description || '',
      assigned_role: task.assigned_role || '', deadline_days: task.deadline_days?.toString() || '',
      requires_document: task.requires_document || false, document_name: task.document_name || '',
      document_template_id: task.document_template_id || '',
    });
    setTaskDialogOpen(true);
  };

  const handleSaveTask = async () => {
    if (!taskForm.name.trim()) { toast.error('Bitte Aufgabennamen eingeben'); return; }
    setSavingTask(true);
    try {
      const taskData = {
        name: taskForm.name, description: taskForm.description || null,
        assigned_role: taskForm.assigned_role === 'none' ? null : (taskForm.assigned_role || null),
        deadline_days: taskForm.deadline_days ? parseInt(taskForm.deadline_days) : null,
        requires_document: taskForm.requires_document,
        document_name: taskForm.requires_document ? (taskForm.document_name || null) : null,
        document_template_id: taskForm.requires_document && taskForm.document_template_id && taskForm.document_template_id !== 'none'
          ? taskForm.document_template_id : null,
        order: editingTask?.order || 0,
      };
      if (editingTask) {
        await workflowAPI.updateTask(selectedTemplateId, editingTask.id, taskData);
        toast.success('Aufgabe aktualisiert');
      } else {
        await workflowAPI.addTask(selectedTemplateId, taskData);
        toast.success('Aufgabe hinzugefügt');
      }
      setTaskDialogOpen(false);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler beim Speichern'); }
    finally { setSavingTask(false); }
  };

  const handleDeleteTask = async (templateId, taskId) => {
    if (!window.confirm('Aufgabe wirklich entfernen?')) return;
    try {
      await workflowAPI.deleteTask(templateId, taskId);
      toast.success('Aufgabe entfernt');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Fehler beim Löschen'); }
  };

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6" data-testid="workflow-templates-page">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Workflow-Vorlagen
            </h1>
            <p className="text-muted-foreground">Vorlagen für alle Workflow-Typen verwalten</p>
          </div>
          {canEdit && (
            <Button onClick={openNewTemplateDialog} data-testid="new-template-btn">
              <Plus className="h-4 w-4 mr-2" />
              Neue Vorlage
            </Button>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="flex flex-wrap h-auto gap-1">
            {WORKFLOW_TYPES.map(wt => {
              const Icon = wt.icon;
              const count = templates.filter(t => t.workflow_type === wt.value).length;
              return (
                <TabsTrigger key={wt.value} value={wt.value} className="gap-1.5 text-xs sm:text-sm" data-testid={`tab-${wt.value}`}>
                  <Icon className={`h-4 w-4 ${wt.color}`} />
                  <span className="hidden sm:inline">{wt.label}</span>
                  {count > 0 && <Badge variant="secondary" className="ml-1 h-5 text-xs">{count}</Badge>}
                </TabsTrigger>
              );
            })}
          </TabsList>

          <TabsContent value={activeTab} className="mt-6 space-y-4">
            {/* Auto-Trigger Settings Card */}
            {isAutoTriggerable && canEdit && (
              <Card className="border-dashed border-primary/30 bg-primary/5" data-testid="auto-trigger-card">
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <Zap className="h-5 w-5 text-primary mt-0.5" />
                      <div>
                        <p className="font-medium text-sm">Automatische Auslösung</p>
                        <p className="text-xs text-muted-foreground">
                          {activeTab === 'probezeit' && 'Workflow startet automatisch vor Ablauf der Probezeit (6 Monate nach Eintritt)'}
                          {activeTab === 'zertifikat' && 'Workflow startet automatisch vor Ablauf von Führungszeugnissen/Zertifikaten'}
                          {activeTab === 'pflichtschulung' && 'Workflow startet jährlich für alle Mitarbeiter'}
                          {activeTab === 'vertragsende' && 'Workflow startet automatisch vor Ablauf der Befristung (employment_end)'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Label htmlFor="trigger-days" className="text-xs whitespace-nowrap">Tage vorher:</Label>
                        <Input
                          id="trigger-days"
                          type="number"
                          min="1"
                          max="365"
                          className="w-20 h-8"
                          value={currentTrigger?.trigger_days_before || 30}
                          onChange={(e) => {
                            setTriggers(prev => prev.map(t =>
                              t.workflow_type === activeTab ? { ...t, trigger_days_before: parseInt(e.target.value) || 30 } : t
                            ));
                          }}
                          onBlur={(e) => handleTriggerDaysChange(e.target.value)}
                          data-testid="trigger-days-input"
                        />
                      </div>
                      <Switch
                        checked={currentTrigger?.enabled || false}
                        onCheckedChange={handleTriggerToggle}
                        disabled={triggerSaving === activeTab}
                        data-testid="trigger-enabled-switch"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {loading ? (
              <div className="space-y-4"><Skeleton className="h-48" /><Skeleton className="h-48" /></div>
            ) : filteredTemplates.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                  <h2 className="text-lg font-semibold mb-2">Keine Vorlagen</h2>
                  <p className="text-muted-foreground mb-4">
                    {canEdit
                      ? `Erstellen Sie Ihre erste ${WORKFLOW_TYPES.find(w => w.value === activeTab)?.label} Vorlage.`
                      : 'Für diesen Typ sind noch keine Vorlagen hinterlegt.'}
                  </p>
                  {canEdit && (
                    <Button onClick={openNewTemplateDialog}>
                      <Plus className="h-4 w-4 mr-2" />Vorlage erstellen
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              filteredTemplates.map((template) => (
                <Card key={template.id} className="border-border/60" data-testid={`template-${template.id}`}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {template.name}
                          {template.is_default && (
                            <Badge className="bg-yellow-500"><Star className="h-3 w-3 mr-1" />Standard</Badge>
                          )}
                        </CardTitle>
                        {template.description && <CardDescription>{template.description}</CardDescription>}
                      </div>
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <Button variant="outline" size="sm" onClick={() => openEditTemplateDialog(template)} data-testid={`edit-template-${template.id}`}>
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDeleteTemplate(template.id)} data-testid={`delete-template-${template.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-medium text-sm text-muted-foreground">
                          Aufgaben ({template.tasks?.length || 0})
                        </h4>
                        {canEdit && (
                          <Button variant="outline" size="sm" onClick={() => openNewTaskDialog(template.id)} data-testid={`add-task-${template.id}`}>
                            <Plus className="h-4 w-4 mr-1" />Aufgabe
                          </Button>
                        )}
                      </div>
                      {template.tasks?.length === 0 ? (
                        <p className="text-sm text-muted-foreground text-center py-4">Noch keine Aufgaben definiert</p>
                      ) : (
                        <DndContext
                          sensors={sensors}
                          collisionDetection={closestCenter}
                          onDragEnd={(event) => handleTaskDragEnd(template, event)}
                        >
                          <SortableContext
                            items={(template.tasks || []).slice().sort((a, b) => a.order - b.order).map(t => t.id)}
                            strategy={verticalListSortingStrategy}
                          >
                            <div className="space-y-2">
                              {template.tasks?.slice().sort((a, b) => a.order - b.order).map((task, index) => (
                                <SortableTaskItem
                                  key={task.id}
                                  task={task}
                                  index={index}
                                  onEdit={(t) => openEditTaskDialog(template.id, t)}
                                  onDelete={(taskId) => handleDeleteTask(template.id, taskId)}
                                  canEdit={canEdit}
                                />
                              ))}
                            </div>
                          </SortableContext>
                        </DndContext>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>

        {/* Template Dialog */}
        <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>{editingTemplate ? 'Vorlage bearbeiten' : 'Neue Vorlage erstellen'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={templateForm.name} onChange={(e) => setTemplateForm({ ...templateForm, name: e.target.value })}
                  placeholder="z.B. Standard Versetzung" data-testid="template-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea value={templateForm.description} onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                  placeholder="Optionale Beschreibung..." rows={2} />
              </div>
              {!editingTemplate && (
                <div className="space-y-2">
                  <Label>Typ</Label>
                  <Select value={templateForm.workflow_type} onValueChange={(v) => setTemplateForm({ ...templateForm, workflow_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {WORKFLOW_TYPES.map(wt => {
                        const Icon = wt.icon;
                        return (
                          <SelectItem key={wt.value} value={wt.value}>
                            <span className="flex items-center gap-2"><Icon className={`h-4 w-4 ${wt.color}`} />{wt.label}</span>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Checkbox id="is-default" checked={templateForm.is_default}
                  onCheckedChange={(checked) => setTemplateForm({ ...templateForm, is_default: !!checked })} />
                <Label htmlFor="is-default" className="cursor-pointer">Als Standard-Vorlage setzen</Label>
              </div>
              <div className="space-y-2">
                <Label>Gültig für (leer = firmenweit)</Label>
                <p className="text-xs text-muted-foreground">Niederlassungen</p>
                <div className="max-h-24 overflow-y-auto rounded-md border p-2 space-y-1" data-testid="template-branches">
                  {branches.length === 0 && <p className="text-xs text-muted-foreground">Keine Niederlassungen</p>}
                  {branches.map(b => (
                    <label key={b.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={templateForm.branch_ids.includes(b.id)}
                        onCheckedChange={(c) => setTemplateForm(f => ({
                          ...f, branch_ids: c ? [...f.branch_ids, b.id] : f.branch_ids.filter(x => x !== b.id),
                        }))}
                        data-testid={`template-branch-${b.id}`}
                      />
                      {b.name}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">Standorte</p>
                <div className="max-h-24 overflow-y-auto rounded-md border p-2 space-y-1" data-testid="template-locations">
                  {locations.length === 0 && <p className="text-xs text-muted-foreground">Keine Standorte</p>}
                  {locations.map(l => (
                    <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <Checkbox
                        checked={templateForm.location_ids.includes(l.id)}
                        onCheckedChange={(c) => setTemplateForm(f => ({
                          ...f, location_ids: c ? [...f.location_ids, l.id] : f.location_ids.filter(x => x !== l.id),
                        }))}
                        data-testid={`template-location-${l.id}`}
                      />
                      <span>{l.name} <span className="text-muted-foreground">({l.branch_name})</span></span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSaveTemplate} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingTemplate ? 'Speichern' : 'Erstellen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Task Dialog */}
        <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingTask ? 'Aufgabe bearbeiten' : 'Neue Aufgabe hinzufügen'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Aufgabenname *</Label>
                <Input value={taskForm.name} onChange={(e) => setTaskForm({ ...taskForm, name: e.target.value })}
                  placeholder="z.B. Arbeitsvertrag unterschreiben" data-testid="task-name-input" />
              </div>
              <div className="space-y-2">
                <Label>Beschreibung</Label>
                <Textarea value={taskForm.description} onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Optionale Details zur Aufgabe..." rows={2} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Zuständig</Label>
                  <Select value={taskForm.assigned_role} onValueChange={(v) => setTaskForm({ ...taskForm, assigned_role: v })}>
                    <SelectTrigger data-testid="task-role-select"><SelectValue placeholder="Rolle wählen" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Keine Zuweisung</SelectItem>
                      {roleDefs.map(role => (
                        <SelectItem key={role.id} value={role.name} data-testid={`task-role-${role.id}`}>{role.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Frist (Tage nach Start)</Label>
                  <Input type="number" min="1" max="365" value={taskForm.deadline_days}
                    onChange={(e) => setTaskForm({ ...taskForm, deadline_days: e.target.value })} placeholder="z.B. 3" />
                </div>
              </div>
              <div className="space-y-3 p-3 bg-secondary/30 rounded-lg">
                <div className="flex items-center gap-2">
                  <Checkbox id="requires-document" checked={taskForm.requires_document}
                    onCheckedChange={(checked) => setTaskForm({ ...taskForm, requires_document: !!checked })} />
                  <Label htmlFor="requires-document" className="cursor-pointer">Dokument erforderlich</Label>
                </div>
                {taskForm.requires_document && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label>Dokumentvorlage (optional)</Label>
                      <Select
                        value={taskForm.document_template_id || 'none'}
                        onValueChange={(v) => setTaskForm({ ...taskForm, document_template_id: v === 'none' ? '' : v })}
                      >
                        <SelectTrigger data-testid="task-doc-template-select"><SelectValue placeholder="Firmen-Dokumentvorlage wählen" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Keine Vorlage (nur Freitext)</SelectItem>
                          {docTemplates.map(dt => (
                            <SelectItem key={dt.id} value={dt.id} data-testid={`task-doc-template-${dt.id}`}>
                              {dt.name}{dt.category ? ` · ${dt.category}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">Aus den Firmen-Dokumentvorlagen. Alternativ/zusätzlich ein freier Dokumentname:</p>
                    </div>
                    <div className="space-y-2">
                      <Label>Dokumentname (Freitext)</Label>
                      <Input value={taskForm.document_name} onChange={(e) => setTaskForm({ ...taskForm, document_name: e.target.value })}
                        placeholder="z.B. Führerschein-Kopie" data-testid="task-doc-name-input" />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setTaskDialogOpen(false)}>Abbrechen</Button>
              <Button onClick={handleSaveTask} disabled={savingTask}>
                {savingTask && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingTask ? 'Speichern' : 'Hinzufügen'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
